<script setup lang="ts">
import { reactive } from 'vue';
import { config, appConfigBuilder, AppConfigLayer } from '@/config';
import { type AppConfigSchema, readOnlyConfigKeys } from '@/config/defaults';
import AccountMenu from '@/components/ui/AccountMenu.vue';
import { useSettingsStore } from '@/stores/settings.store';
import InputConfigValue from '@/components/ui/InputConfigValue.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import { JsonStorageProvider } from '@/config/providers/JsonStorageProvider';
import { prettifyConfigKey } from '@/config/prettifyConfigKey';

// Opts out of SidebarLayout — no app nav on this page — which means it has
// to wire up the account menu itself.
const settingsStore = useSettingsStore();

// To give visibility on values coming from the server
const serverLayer = appConfigBuilder.getLayer(AppConfigLayer.WINDOW);
const localLayer = appConfigBuilder.getLayer(AppConfigLayer.LOCAL_STORAGE);
const localLayerProvider = localLayer?.provider as JsonStorageProvider<AppConfigSchema> | undefined;

// Form values
const formValues = reactive<AppConfigSchema>({ ...config });

function resetConfig() {
  localLayerProvider?.reset();
  location.reload();
}

function persistConfig() {
  Object.entries(formValues).forEach(([key, value]) => {
    if (value !== config[key as keyof AppConfigSchema]) {
      localLayerProvider?.saveValue(key, value);
    }
  });
  location.reload();
}
</script>

<template>
  <div class="h-screen overflow-auto bg-white">
    <AccountMenu
      :theme="settingsStore.preference"
      :local-dates="settingsStore.localDates"
      @toggle-theme="settingsStore.toggleTheme()"
      @update:local-dates="settingsStore.setLocalDates"
    />

    <div class="mx-auto w-[800px] max-w-full px-4">
      <p class="py-5 text-center text-xl font-bold text-gray-900" data-test="screen-config">
        Development Config
      </p>

      <div class="mb-5 flex flex-col items-center md:flex-row md:justify-between">
        <BaseButton text="Reset" @click="resetConfig" class="my-2" data-test="btn-reset-top" />
        <BaseButton text="Save" @click="persistConfig" class="my-2" data-test="btn-save-top" />
      </div>

      <ul>
        <li v-for="(_, key) in config" :key="key">
          <InputConfigValue
            :item-key="key"
            :display-value="prettifyConfigKey(key)"
            v-model="formValues[key]"
            :currentValue="config[key]"
            :baseValue="
              serverLayer?.provider.getValue(key) !== undefined
                ? (serverLayer?.provider.getValue(key) as string | boolean | number)
                : ''
            "
            :readOnly="readOnlyConfigKeys.includes(key)"
          />
        </li>
      </ul>

      <div class="my-5 flex flex-col items-center md:flex-row md:justify-between">
        <BaseButton text="Reset" @click="resetConfig" class="my-2" data-test="btn-reset-bottom" />
        <BaseButton text="Save" @click="persistConfig" class="my-2" data-test="btn-save-bottom" />
      </div>
    </div>
  </div>
</template>
